<template>
    <div v-if="active" :class="wrapperClass">
        <CompassIconButton
            icon="caret-left"
            :size="iconButtonSize"
            :disabled="!prevUrl"
            @click="goTo(prevUrl)"
        />
        <span :class="labelClass">{{ humanNameType(identity.type) | capitalize }}</span>
        <CompassIconButton
            icon="caret-right"
            :size="iconButtonSize"
            :disabled="!nextUrl"
            @click="goTo(nextUrl)"
        />
    </div>
</template>

<script>
import SessionStorage from '../../../core/commons/SessionStorage';
import LocalStorage from 'Core/commons/LocalStorage';
import HumanNameTypeMixin from '../../mixins/HumanNameTypeMixins';
import { goToUrl } from '../../helpers';
import Feature from 'Services/Feature';

export default {
    context: null,
    storage: null,
    mixins: [HumanNameTypeMixin],
    props: {
        overviewhash: {
            type: String,
            required: true,
        },
        identity: {
            type: Object,
            required: true,
        },
        splitter: {
            type: RegExp,
            required: true,
        },
        useSPARouting: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            active: false,
            listingName: '',
            contextId: '',
            curIndex: null,
            nextUrl: null,
            prevUrl: null,
        };
    },
    computed: {
        showNewVersion() {
            return Feature.isEnabled('overview-info-sidebar');
        },
        labelClass() {
            return this.showNewVersion ? 'typographies-paragraph-small-semibold' : 'fs-4 px-2';
        },
        wrapperClass() {
            return this.showNewVersion ? 'overview-nav' : 'flex-level';
        },
        iconButtonSize() {
            return this.showNewVersion ? 'extra-small' : 'small';
        },
        fullNextUrl() {
            const url = this.nextUrl ? this.nextUrl : '';
            return typeof this.$store.getters.tabSelected !== 'undefined'
                ? url + '#tabSelected=' + this.$store.getters.tabSelected
                : url;
        },
        fullPrevUrl() {
            const url = this.prevUrl ? this.prevUrl : '';
            return typeof this.$store.getters.tabSelected !== 'undefined'
                ? url + '#tabSelected=' + this.$store.getters.tabSelected
                : url;
        },
    },
    created() {
        this.updateUrls();

        this.unsubscribe = this.$store.subscribe((mutation, state) => {
            if (mutation.type === 'updateSelectedTab') {
                this.updateUrls();
            }
        });
    },
    beforeDestroy() {
        this.unsubscribe();
    },
    methods: {
        updateUrls() {
            //Get context from session or local storage
            if (!this.loadContext()) {
                return;
            }

            let fetch;
            if (this.curIndex === 0) {
                //fetch previous
                fetch = this.fetchPrevious();
            } else if (this.curIndex === this.context.ids.length - 1) {
                //fetch next
                fetch = this.fetchNext();
            } else {
                fetch = Promise.resolve();
            }

            fetch
                .then(() => {
                    this.findIndex(); // Refresh curIndex
                    this.saveContext();

                    this.nextUrl = this.getUrlByIndex(this.curIndex + 1);
                    this.prevUrl = this.getUrlByIndex(this.curIndex - 1);

                    this.active = true;
                    this.loading = false;
                })
                .catch((e) => {
                    this.loading = false;
                });
        },
        goToUrl,
        goTo(url) {
            if (this.useSPARouting) {
                url && url != '' ? this.$router.push({ path: url }) : null;
            } else {
                this.goToUrl(url);
            }
        },
        /**
         * Load context from local / session storage
         * @returns {boolean}
         */
        loadContext() {
            const location = window.location;
            const urlParams = new URLSearchParams(location.search);
            this.contextId = urlParams.get('contextId');

            if (!this.contextId) {
                return false;
            }

            const splittedContext = this.contextId.split('_');
            this.listingName = splittedContext[0];

            // Load & check overview context
            this.storage = new SessionStorage('overview_context', this.contextId);
            this.context = this.storage.get();
            if (this.findIndex()) {
                return true;
            }

            // Load & check listing context
            const key =
                splittedContext.length === 3
                    ? this.listingName + '_' + splittedContext[2]
                    : this.listingName;
            this.context = new LocalStorage('listing_context', key).get();

            return this.findIndex();
        },

        /**
         * Fetch previous page of listing
         * @returns {Promise<unknown>}
         */
        fetchPrevious() {
            return new Promise((resolve, reject) => {
                const curPage = Math.min(...this.context.pages);

                if (curPage === 1) {
                    //already first page loaded
                    return resolve();
                }

                this.fetchListing(curPage - 1)
                    .then((newIds) => {
                        this.context.ids = [...newIds, ...this.context.ids];
                        this.context.pages.push(curPage - 1);
                        resolve();
                    })
                    .catch(reject);
            });
        },

        /**
         * Fetch next page of listing
         * @returns {Promise<unknown>}
         */
        fetchNext() {
            return new Promise((resolve, reject) => {
                const curPage = Math.max(...this.context.pages);
                if (curPage === this.context.meta.pagination.last_page) {
                    //already last page loaded
                    return resolve();
                }

                this.fetchListing(curPage + 1)
                    .then((newIds) => {
                        this.context.ids = [...this.context.ids, ...newIds];
                        this.context.pages.push(curPage + 1);
                        resolve();
                    })
                    .catch(reject);
            });
        },

        /**
         * Fetch listing
         * @param int page
         * @returns {Promise<unknown>}
         */
        fetchListing(page) {
            this.loading = true;

            const params = {
                init: false,
                meta: this.context.meta,
                persist: false,
            };
            params.meta.pagination.current_page = page;

            return new Promise((resolve, reject) => {
                this.$ajax.post(`/listing/${this.listingName}/ids`, {}, params, resolve, reject);
            });
        },

        /**
         * Find index of current overview id in context.ids
         * @returns {boolean}
         */
        findIndex() {
            if (!this.context) {
                return false;
            }

            const curIndex = this.context.ids.indexOf(this.identity.id.toString());
            if (curIndex === -1) {
                return false;
            }

            this.curIndex = curIndex;
            return true;
        },

        /**
         * Save context in sessionStorage
         */
        saveContext() {
            this.storage.set(this.context);
        },

        /**
         * Format overview url
         * @param id
         * @returns {string}
         */
        overviewUrl(id) {
            const url = window.location.pathname + window.location.search;
            const idParts = id.split('_');

            const match = this.splitter.exec(url);

            if (!match) return url;

            let rebuiltUrl = '';
            let lastIndex = 0;

            // match[0] est la chaîne entière qui matche, match[1..n] les groupes capturants
            for (let i = 1; i < match.length; i++) {
                const groupValue = match[i];
                if (groupValue === undefined) continue;

                const groupStart = match[0].indexOf(groupValue, lastIndex);
                const groupEnd = groupStart + groupValue.length;

                // On ajoute la partie avant le groupe, puis la nouvelle valeur
                rebuiltUrl += match[0].slice(lastIndex, groupStart) + (idParts[i - 1] || '');
                lastIndex = groupEnd;
            }

            // Ajouter la fin du match s'il en reste
            rebuiltUrl += match[0].slice(lastIndex);

            // Remplacer le match initial dans l'URL
            return url.replace(match[0], rebuiltUrl);
        },

        /**
         * Get url by context ids index
         * @param index
         * @returns {string|boolean}
         */
        getUrlByIndex(index) {
            if (index < 0 || index > this.context.ids.length - 1) {
                return false;
            }

            return this.overviewUrl(this.context.ids[index]);
        },
    },
};
</script>

<style lang="scss" scoped>
.overview-nav {
    align-items: center;
    color: var(--colors-common-text-subtle-default);
    display: flex;
    gap: var(--spacing-0);
}
</style>
